import { useCallback, useEffect, useId, useRef, useState } from 'react';

export interface RichTextEditorProps {
  readonly id?: string | undefined;
  readonly label: string;
  readonly hint?: string | undefined;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly maxLength?: number | undefined;
  readonly placeholder?: string | undefined;
  readonly disabled?: boolean | undefined;
}

/** Extracts readable plain text character count from HTML. */
export function getPlainTextLength(html: string): number {
  if (!html) return 0;
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return (temp.textContent || temp.innerText || '').trim().length;
}

export function RichTextEditor({
  id: customId,
  label,
  hint,
  value,
  onChange,
  maxLength = 2000,
  placeholder = 'Add an optional note to the contact explaining what is needed...',
  disabled = false,
}: RichTextEditorProps): React.JSX.Element {
  const generatedId = useId();
  const id = customId ?? generatedId;
  const editorRef = useRef<HTMLDivElement>(null);
  const [charCount, setCharCount] = useState(() => getPlainTextLength(value));

  // Sync external value to innerHTML when value changes from outside (e.g. reset)
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
      setCharCount(getPlainTextLength(value));
    }
  }, [value]);

  const handleInput = useCallback(() => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    const count = getPlainTextLength(html);
    setCharCount(count);
    onChange(html);
  }, [onChange]);

  const exec = useCallback((command: string, value: string | undefined = undefined) => {
    if (disabled) return;
    // execCommand remains the only broadly supported editing API for contenteditable formatting.
    const execCommand = Reflect.get(globalThis.document, 'execCommand') as (
      commandId: string,
      showUI?: boolean,
      commandValue?: string,
    ) => boolean;
    execCommand.call(globalThis.document, command, false, value);
    if (editorRef.current) {
      editorRef.current.focus();
      const html = editorRef.current.innerHTML;
      setCharCount(getPlainTextLength(html));
      onChange(html);
    }
  }, [disabled, onChange]);

  const handleAddLink = useCallback(() => {
    if (disabled) return;
    const url = globalThis.prompt('Enter link URL (https://...):');
    if (url && url.trim() !== '') {
      const trimmed = url.trim();
      const finalUrl = /^https?:\/\//i.test(trimmed) || /^mailto:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
      exec('createLink', finalUrl);
    }
  }, [disabled, exec]);

  const isOverLimit = charCount > maxLength;
  const hintId = `${id}-hint`;
  const metaId = `${id}-meta`;

  return (
    <div className="ddds-rich-editor">
      <label className="ddds-field__label" htmlFor={`${id}-editor`}>
        {label}
      </label>
      {hint !== undefined && (
        <span className="ddds-field__hint" id={hintId}>
          {hint}
        </span>
      )}

      <div className="ddds-rich-editor__toolbar" role="toolbar" aria-label="Rich text formatting">
        <button
          type="button"
          className="ddds-rich-editor__tool-btn"
          title="Bold (Ctrl+B)"
          disabled={disabled}
          onClick={() => exec('bold')}
          aria-label="Bold"
        >
          <b>B</b>
        </button>
        <button
          type="button"
          className="ddds-rich-editor__tool-btn"
          title="Italic (Ctrl+I)"
          disabled={disabled}
          onClick={() => exec('italic')}
          aria-label="Italic"
        >
          <i>I</i>
        </button>
        <button
          type="button"
          className="ddds-rich-editor__tool-btn"
          title="Underline (Ctrl+U)"
          disabled={disabled}
          onClick={() => exec('underline')}
          aria-label="Underline"
        >
          <u>U</u>
        </button>
        <button
          type="button"
          className="ddds-rich-editor__tool-btn"
          title="Bullet List"
          disabled={disabled}
          onClick={() => exec('insertUnorderedList')}
          aria-label="Bullet List"
        >
          &bull; List
        </button>
        <button
          type="button"
          className="ddds-rich-editor__tool-btn"
          title="Numbered List"
          disabled={disabled}
          onClick={() => exec('insertOrderedList')}
          aria-label="Numbered List"
        >
          1. List
        </button>
        <button
          type="button"
          className="ddds-rich-editor__tool-btn"
          title="Insert Link"
          disabled={disabled}
          onClick={handleAddLink}
          aria-label="Insert Link"
        >
          Link
        </button>
      </div>

      <div
        id={`${id}-editor`}
        ref={editorRef}
        className="ddds-rich-editor__content"
        contentEditable={!disabled}
        aria-multiline="true"
        aria-label={label}
        aria-describedby={[hint ? hintId : null, metaId].filter(Boolean).join(' ')}
        data-placeholder={placeholder}
        onInput={handleInput}
      />

      <div className="ddds-rich-editor__meta" id={metaId}>
        <span>Optional note included with the request</span>
        <span className={isOverLimit ? 'ddds-rich-editor__counter--exceeded' : undefined}>
          {charCount.toLocaleString()} / {maxLength.toLocaleString()} characters
        </span>
      </div>
    </div>
  );
}
